import * as A from 'fp-ts/es6/Array'; // TODO: replace with ReadonlyArray in v3
import * as R from 'fp-ts/es6/Record'; // TODO: replace with ReadonlyRecord in v3
import { constant, flow, identity } from 'fp-ts/es6/function';
import * as O from 'fp-ts/es6/Option';
import * as E from 'fp-ts/es6/Either';
import { pipe } from 'fp-ts/es6/pipeable';
// -------------------------------------------------------------------------------------
// Iso
// -------------------------------------------------------------------------------------
/** @internal */
export var isoAsLens = function (sa) { return ({
    get: sa.get,
    set: flow(sa.reverseGet, constant)
}); };
/** @internal */
export var isoAsOptional = function (sa) { return ({
    getOption: flow(sa.get, O.some),
    set: flow(sa.reverseGet, constant)
}); };
// -------------------------------------------------------------------------------------
// Lens
// -------------------------------------------------------------------------------------
/** @internal */
export var lensAsOptional = function (sa) { return ({
    getOption: flow(sa.get, O.some),
    set: sa.set
}); };
/** @internal */
export var lensAsTraversal = function (sa) { return ({
    modifyF: function (F) { return function (f) { return function (s) { return F.map(f(sa.get(s)), function (a) { return sa.set(a)(s); }); }; }; }
}); };
/** @internal */
export var lensComposeLens = function (ab) { return function (sa) { return ({
    get: function (s) { return ab.get(sa.get(s)); },
    set: function (b) { return function (s) { return sa.set(ab.set(b)(sa.get(s)))(s); }; }
}); }; };
/** @internal */
export var lensComposePrism = function (ab) { return function (sa) {
    return optionalComposeOptional(prismAsOptional(ab))(lensAsOptional(sa));
}; };
/** @internal */
export var lensId = function () { return ({
    get: identity,
    set: constant
}); };
/** @internal */
export var lensProp = function (prop) { return function (lens) { return ({
    get: function (s) { return lens.get(s)[prop]; },
    set: function (ap) { return function (s) {
        var _a;
        var oa = lens.get(s);
        if (ap === oa[prop]) {
            return s;
        }
        return lens.set(Object.assign({}, oa, (_a = {}, _a[prop] = ap, _a)))(s);
    }; }
}); }; };
/** @internal */
export var lensProps = function () {
    var props = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        props[_i] = arguments[_i];
    }
    return function (lens) { return ({
        get: function (s) {
            var a = lens.get(s);
            var r = {};
            for (var _i = 0, props_1 = props; _i < props_1.length; _i++) {
                var k = props_1[_i];
                r[k] = a[k];
            }
            return r;
        },
        set: function (a) { return function (s) {
            var oa = lens.get(s);
            for (var _i = 0, props_2 = props; _i < props_2.length; _i++) {
                var k = props_2[_i];
                if (a[k] !== oa[k]) {
                    return lens.set(Object.assign({}, oa, a))(s);
                }
            }
            return s;
        }; }
    }); };
};
/** @internal */
export var lensComponent = function (prop) { return function (lens) { return ({
    get: function (s) { return lens.get(s)[prop]; },
    set: function (ap) { return function (s) {
        var oa = lens.get(s);
        if (ap === oa[prop]) {
            return s;
        }
        var copy = oa.slice();
        copy[prop] = ap;
        return lens.set(copy)(s);
    }; }
}); }; };
// -------------------------------------------------------------------------------------
// Prism
// -------------------------------------------------------------------------------------
/** @internal */
export var prismAsOptional = function (sa) { return ({
    getOption: sa.getOption,
    set: function (a) { return prismSet(a)(sa); }
}); };
/** @internal */
export var prismAsTraversal = function (sa) { return ({
    modifyF: function (F) { return function (f) { return function (s) {
        return pipe(sa.getOption(s), O.fold(function () { return F.of(s); }, function (a) { return F.map(f(a), function (a) { return prismSet(a)(sa)(s); }); }));
    }; }; }
}); };
/** @internal */
export var prismModifyOption = function (f) { return function (sa) { return function (s) {
    return pipe(sa.getOption(s), O.map(function (o) {
        var n = f(o);
        return n === o ? s : sa.reverseGet(n);
    }));
}; }; };
/** @internal */
export var prismModify = function (f) { return function (sa) {
    var g = prismModifyOption(f)(sa);
    return function (s) {
        return pipe(g(s), O.getOrElse(function () { return s; }));
    };
}; };
/** @internal */
export var prismSet = function (a) { return prismModify(function () { return a; }); };
/** @internal */
export var prismComposeLens = function (ab) { return function (sa) {
    return optionalComposeOptional(lensAsOptional(ab))(prismAsOptional(sa));
}; };
/** @internal */
export var prismFromNullable = function () { return ({
    getOption: O.fromNullable,
    reverseGet: identity
}); };
/** @internal */
export function prismFromPredicate(predicate) {
    return {
        getOption: O.fromPredicate(predicate),
        reverseGet: identity
    };
}
/** @internal */
export var prismSome = function () { return ({
    getOption: identity,
    reverseGet: O.some
}); };
/** @internal */
export var prismRight = function () { return ({
    getOption: O.fromEither,
    reverseGet: E.right
}); };
/** @internal */
export var prismLeft = function () { return ({
    getOption: function (s) { return (E.isLeft(s) ? O.some(s.left) : O.none); },
    reverseGet: E.left
}); };
// -------------------------------------------------------------------------------------
// Optional
// -------------------------------------------------------------------------------------
/** @internal */
export var optionalAsTraversal = function (sa) { return ({
    modifyF: function (F) { return function (f) { return function (s) {
        return pipe(sa.getOption(s), O.fold(function () { return F.of(s); }, function (a) { return F.map(f(a), function (a) { return sa.set(a)(s); }); }));
    }; }; }
}); };
/** @internal */
export var optionalModifyOption = function (f) { return function (optional) { return function (s) {
    return pipe(optional.getOption(s), O.map(function (a) {
        var n = f(a);
        return n === a ? s : optional.set(n)(s);
    }));
}; }; };
/** @internal */
export var optionalModify = function (f) { return function (optional) {
    var g = optionalModifyOption(f)(optional);
    return function (s) {
        return pipe(g(s), O.getOrElse(function () { return s; }));
    };
}; };
/** @internal */
export var optionalComposeOptional = function (ab) { return function (sa) { return ({
    getOption: flow(sa.getOption, O.chain(ab.getOption)),
    set: function (b) { return optionalModify(ab.set(b))(sa); }
}); }; };
var findFirstMutable = function (predicate) { return ({
    getOption: A.findFirst(predicate),
    set: function (a) { return function (s) {
        return pipe(A.findIndex(predicate)(s), O.fold(function () { return s; }, function (i) { return A.unsafeUpdateAt(i, a, s); }));
    }; }
}); };
/** @internal */
export var findFirst = findFirstMutable;
// -------------------------------------------------------------------------------------
// Traversal
// -------------------------------------------------------------------------------------
/** @internal */
export function traversalComposeTraversal(ab) {
    return function (sa) { return ({
        modifyF: function (F) { return function (f) { return sa.modifyF(F)(ab.modifyF(F)(f)); }; }
    }); };
}
/** @internal */
export function fromTraversable(T) {
    return function () { return ({
        modifyF: function (F) {
            var traverseF = T.traverse(F);
            return function (f) { return function (s) { return traverseF(s, f); }; };
        }
    }); };
}
// -------------------------------------------------------------------------------------
// Ix
// -------------------------------------------------------------------------------------
function indexMutableArray() {
    return {
        index: function (i) { return ({
            getOption: function (as) { return A.lookup(i, as); },
            set: function (a) { return function (as) {
                return pipe(A.updateAt(i, a)(as), O.getOrElse(function () { return as; }));
            }; }
        }); }
    };
}
/** @internal */
export var indexArray = indexMutableArray;
/** @internal */
export function indexRecord() {
    return {
        index: function (k) { return ({
            getOption: function (r) { return R.lookup(k, r); },
            set: function (a) { return function (r) {
                if (r[k] === a || O.isNone(R.lookup(k, r))) {
                    return r;
                }
                return R.insertAt(k, a)(r);
            }; }
        }); }
    };
}
// -------------------------------------------------------------------------------------
// At
// -------------------------------------------------------------------------------------
/** @internal */
export function atRecord() {
    return {
        at: function (key) { return ({
            get: function (r) { return R.lookup(key, r); },
            set: O.fold(function () { return R.deleteAt(key); }, function (a) { return R.insertAt(key, a); })
        }); }
    };
}
